import { useEffect, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import { safeMarkdownProps } from '../lib/safeMarkdown';
import { KianUsageLine, kianUsageFrom } from './KianUsageLine';
import remarkGfm from 'remark-gfm';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrain,
  IconCheck,
  IconClock,
  IconInfoCircle,
  IconRefresh,
  IconSend,
  IconShieldExclamation,
  IconSparkles,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../lib/api';
import type { KianAnalysis, KianAnalysisDiagnosis } from '../lib/api/domains/cockpit';

interface Props {
  ticketId: number | string;
  source: 'CW' | 'WECLAPP';
  /** ISO string from ticket detail — used to decide if auto-trigger applies */
  ticketEnteredAt?: string;
  /**
   * Kompakt-Modus: nur eine kleine Button-Zeile (Analysieren / Deep / (i)-Hinweis) —
   * für den Ticket-Kopf, solange noch keine Analyse existiert. Die volle Spalte
   * erscheint erst, wenn tatsächlich Inhalt da ist.
   */
  compact?: boolean;
}

const ONE_HOUR_MS = 60 * 60 * 1000;

function isTicketNew(enteredAt?: string): boolean {
  if (!enteredAt) return false;
  return Date.now() - new Date(enteredAt).getTime() < ONE_HOUR_MS;
}

function isLimitCode(code: KianAnalysisDiagnosis['code']): boolean {
  return code === 'customer_hourly_limit' || code === 'daily_limit';
}

/**
 * Farbe und Symbol nach Art des Befunds: ein Betriebsproblem (Queue tot, Job hängt) soll
 * anders aussehen als „läuft gleich" oder „für dieses Ticket war nie einer vorgesehen".
 */
function diagnosisTone(diagnosis: KianAnalysisDiagnosis | null): {
  color: string;
  icon: ReactNode;
} {
  switch (diagnosis?.code) {
    case 'queue_unavailable':
    case 'job_stuck_no_worker':
    case 'job_failed':
      return { color: 'red', icon: <IconAlertTriangle size={16} /> };
    case 'job_pending':
      return { color: 'blue', icon: <IconClock size={16} /> };
    case 'customer_hourly_limit':
    case 'daily_limit':
    case 'ticket_too_old':
    case 'not_mirrored':
      return { color: 'yellow', icon: <IconInfoCircle size={16} /> };
    default:
      return { color: 'gray', icon: <IconInfoCircle size={16} /> };
  }
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** contextMeta ist ein untypisierter JSON-Blob — Zahl defensiv herausziehen. */
function metaNumber(meta: Record<string, unknown> | null, key: string): number {
  const v = meta?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Warnhinweis, wenn der Injection-Scan in den Quelldaten etwas gefunden hat:
 * rot bei blockierten Inhalten, gelb bei nur auffälligen Mustern.
 */
export function InjectionWarningAlert({
  blocked,
  flagged,
  subjectGenitive = 'dieser Analyse',
}: {
  blocked: number;
  flagged: number;
  /** z. B. „dieser Analyse" oder „dieses Berichts" — für den Satzbau. */
  subjectGenitive?: string;
}) {
  if (blocked <= 0 && flagged <= 0) return null;
  return (
    <Alert
      color={blocked > 0 ? 'red' : 'yellow'}
      variant="light"
      p="xs"
      icon={<IconShieldExclamation size={16} />}
    >
      <Text size="xs">
        {blocked > 0
          ? `In den Quelldaten ${subjectGenitive} wurde mutmaßliche Prompt-Injection blockiert. Inhalte mit Vorsicht behandeln — Details im Admin-Injection-Log.`
          : `In den Quelldaten ${subjectGenitive} wurden auffällige Muster erkannt (mögliche Prompt-Injection). Inhalte mit Vorsicht behandeln — Details im Admin-Injection-Log.`}
      </Text>
    </Alert>
  );
}

export function KianAnalysisPanel({ ticketId, source, ticketEnteredAt, compact = false }: Props) {
  const queryClient = useQueryClient();
  const queryKey = ['kian-analysis', ticketId, source] as const;
  const [deepModalOpen, setDeepModalOpen] = useState(false);
  const [triggerComment, setTriggerComment] = useState('');
  const [activeMode, setActiveMode] = useState<'STANDARD' | 'DEEP' | null>(null);
  const autoAttempted = useRef(false);

  const { data, isLoading: isFetching } = useQuery({
    queryKey,
    queryFn: () => api.getKianAnalysis(ticketId, source),
    staleTime: 30_000,
  });

  const analysis: KianAnalysis | null = data?.analysis ?? null;

  /**
   * Fehlt das Briefing, ist die Frage immer dieselbe: warum? Vorher stand hier für jeden
   * Grund derselbe Satz. Die Diagnose wird deshalb genau dann geholt, wenn nichts da ist —
   * und nicht auf Vorrat für jedes geöffnete Ticket.
   */
  const { data: diagnosisData } = useQuery({
    queryKey: ['kian-analysis-diagnosis', ticketId, source] as const,
    queryFn: () => api.getKianAnalysisDiagnosis(ticketId, source),
    enabled: !isFetching && !analysis,
    staleTime: 30_000,
    retry: false,
  });
  const diagnosis: KianAnalysisDiagnosis | null = diagnosisData?.diagnosis ?? null;

  const triggerMutation = useMutation({
    mutationFn: (opts: {
      mode: 'STANDARD' | 'DEEP';
      triggerComment?: string;
      autoTrigger?: boolean;
    }) =>
      api.triggerKianAnalysis(ticketId, {
        source,
        mode: opts.mode,
        triggerComment: opts.triggerComment,
        autoTrigger: opts.autoTrigger ?? false,
        ticketEnteredAt,
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(queryKey, { analysis: res.analysis });
      void queryClient.invalidateQueries({
        queryKey: ['kian-analysis-diagnosis', ticketId, source],
      });
      setDeepModalOpen(false);
      setTriggerComment('');
      setActiveMode(null);
    },
    onError: (err: Error & { status?: number }) => {
      setActiveMode(null);
      // 429 from rate limiting = silent, just don't auto-analyze
      if ((err as { status?: number }).status === 429) return;
      notifications.show({ color: 'red', message: `Analyse fehlgeschlagen: ${err.message}` });
    },
  });

  const postMutation = useMutation({
    mutationFn: (analysisId: string) => api.postKianAnalysisToCw(ticketId, analysisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notifications.show({
        color: 'green',
        message: source === 'CW' ? 'Als interne CW-Notiz gepostet.' : 'Als interner Weclapp-Kommentar gepostet.',
      });
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Fehler beim Posten: ${err.message}` });
    },
  });

  // Smart auto-trigger: only for tickets < 1h old, once per mount
  useEffect(() => {
    if (
      !isFetching &&
      !analysis &&
      !autoAttempted.current &&
      !triggerMutation.isPending &&
      isTicketNew(ticketEnteredAt)
    ) {
      autoAttempted.current = true;
      setActiveMode('STANDARD');
      triggerMutation.mutate({ mode: 'STANDARD', autoTrigger: true });
    }
  }, [isFetching, analysis, ticketEnteredAt, triggerMutation]);

  const isAnalyzing = triggerMutation.isPending;
  const isNewTicket = isTicketNew(ticketEnteredAt);

  const deepModal = (
    <Modal
      opened={deepModalOpen}
      onClose={() => setDeepModalOpen(false)}
      title={
        <Group gap="xs">
          <IconSparkles size={18} color="var(--mantine-color-violet-6)" />
          <Text fw={600}>Deep-Analyse starten</Text>
        </Group>
      }
      size="md"
      zIndex={700}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Die Deep-Analyse verwendet das leistungsstärkste Modell mit vollem Kontext. Du kannst
          optional einen Hinweis für KIAN angeben.
        </Text>
        <Textarea
          label="Hinweis für KIAN (optional)"
          placeholder="z. B. Bitte besonders auf Netzwerk-Komponenten eingehen…"
          value={triggerComment}
          onChange={(e) => setTriggerComment(e.currentTarget.value)}
          minRows={3}
          maxRows={6}
          autosize
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeepModalOpen(false)}>
            Abbrechen
          </Button>
          <Button
            color="violet"
            leftSection={<IconSparkles size={15} />}
            onClick={() => {
              setActiveMode('DEEP');
              // Sofort schließen: der Fortschritt wird im Panel angezeigt. Bliebe das
              // Modal offen, drehte sich zusätzlich hier ein Spinner.
              setDeepModalOpen(false);
              triggerMutation.mutate({
                mode: 'DEEP',
                triggerComment: triggerComment.trim() || undefined,
              });
            }}
          >
            Deep-Analyse starten
          </Button>
        </Group>
      </Stack>
    </Modal>
  );

  if (compact) {
    return (
      <>
        <Group gap={4} wrap="nowrap">
          <Tooltip label="KIAN-Analyse jetzt erstellen (Standard-Modell)">
            <Button
              size="compact-xs"
              variant="light"
              color="violet"
              leftSection={<IconBrain size={13} />}
              loading={isAnalyzing}
              onClick={() => {
                setActiveMode('STANDARD');
                triggerMutation.mutate({ mode: 'STANDARD' });
              }}
            >
              {isAnalyzing ? (activeMode === 'DEEP' ? 'KIAN (Deep)…' : 'KIAN…') : 'KIAN-Analyse'}
            </Button>
          </Tooltip>
          <Tooltip label="Deep-Analyse: stärkstes Modell, voller Kontext, optionaler Hinweis an KIAN">
            <Button
              size="compact-xs"
              variant="subtle"
              color="violet"
              leftSection={<IconSparkles size={12} />}
              disabled={isAnalyzing}
              onClick={() => setDeepModalOpen(true)}
            >
              Deep
            </Button>
          </Tooltip>
          <Tooltip
            multiline
            w={300}
            withArrow
            label={
              diagnosis?.message ??
              'Noch keine KIAN-Analyse vorhanden. Automatisch analysiert werden nur neue Tickets — ältere per Klick auf „KIAN-Analyse".'
            }
          >
            <ActionIcon size="sm" variant="subtle" color="gray" aria-label="Warum gibt es noch keine Analyse?">
              <IconInfoCircle size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
        {deepModal}
      </>
    );
  }

  return (
    <Paper withBorder p="sm" radius="md" style={{ borderColor: 'var(--mantine-color-violet-3)' }}>
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconBrain size={16} color="var(--mantine-color-violet-6)" />
            <Text size="sm" fw={600} c="violet.7">
              KIAN-Analyse
            </Text>
          </Group>

          {analysis && (
            <Group gap={6}>
              <Badge size="xs" variant="light" color={analysis.mode === 'DEEP' ? 'violet' : 'blue'}>
                {analysis.mode}
              </Badge>
              {analysis.autoTriggered && (
                <Badge size="xs" variant="dot" color="teal">auto</Badge>
              )}
              <Badge size="xs" variant="outline" color="gray">
                {formatShortDate(analysis.createdAt)}
              </Badge>
              {analysis.postedAt && (
                <Badge size="xs" variant="light" color="green" leftSection={<IconCheck size={10} />}>
                  Gepostet
                </Badge>
              )}
            </Group>
          )}
        </Group>

        {/* Loading / Analyzing state */}
        {(isFetching || isAnalyzing) && (
          <Group gap="xs" justify="center" py="md">
            <Loader size="sm" color="violet" />
            <Text size="xs" c="dimmed">
              {isAnalyzing
                ? activeMode === 'DEEP'
                  ? 'KIAN analysiert (Deep)…'
                  : 'KIAN analysiert…'
                : 'Lade…'}
            </Text>
          </Group>
        )}

        {/* No analysis yet + ticket not new → show manual trigger */}
        {!isAnalyzing && !isFetching && !analysis && !isNewTicket && (
          <Alert
            color={diagnosisTone(diagnosis).color}
            variant="light"
            p="xs"
            icon={diagnosisTone(diagnosis).icon}
          >
            <Stack gap={4}>
              <Text size="xs" c={diagnosis ? undefined : 'dimmed'}>
                {diagnosis?.message ??
                  'Noch keine KIAN-Analyse vorhanden — mit „Analysieren“ jetzt erstellen (neue Tickets werden automatisch analysiert).'}
              </Text>
              {diagnosis?.detail && (
                <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                  {diagnosis.detail}
                </Text>
              )}
              {/* Zähler nur zeigen, wenn ein Limit die Ursache ist — sonst sind sie Rauschen. */}
              {diagnosis && isLimitCode(diagnosis.code) && (
                <Text size="xs" c="dimmed">
                  Kunde in der letzten Stunde: {diagnosis.counters.customerLastHour}/
                  {diagnosis.counters.customerHourlyLimit} · gesamt in 24 h:{' '}
                  {diagnosis.counters.globalLast24h}/{diagnosis.counters.globalDailyLimit}
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        {/* Injection-Warnung: Quelldaten enthielten mutmaßliche Prompt-Injection */}
        {!isAnalyzing && analysis && (
          <InjectionWarningAlert
            blocked={metaNumber(analysis.contextMeta, 'injectionBlocked')}
            flagged={metaNumber(analysis.contextMeta, 'injectionFlagged')}
          />
        )}

        {/* Analysis content */}
        {!isAnalyzing && analysis && (
          <Box style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.6 }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              {...safeMarkdownProps}
              components={{
                p: ({ children }) => <Text size="sm" mb={4}>{children}</Text>,
                strong: ({ children }) => (
                  <Text component="span" size="sm" fw={700}>{children}</Text>
                ),
                li: ({ children }) => (
                  <Text component="li" size="sm" style={{ marginLeft: 16, marginBottom: 2 }}>
                    {children}
                  </Text>
                ),
                h2: ({ children }) => (
                  <Text size="sm" fw={700} mt={8} mb={2} c="violet.7">{children}</Text>
                ),
                h3: ({ children }) => (
                  <Text size="sm" fw={700} mt={6} mb={2}>{children}</Text>
                ),
              }}
            >
              {analysis.analysisText}
            </ReactMarkdown>
          </Box>
        )}
        {!isAnalyzing && analysis && <KianUsageLine usage={kianUsageFrom(analysis.contextMeta?.llmUsage)} />}

        {/* Actions */}
        <Group gap="xs" justify="flex-end" mt={4}>
          {analysis && !analysis.postedAt && (
            <Tooltip label="Automatisches Posting fehlgeschlagen — manuell erneut versuchen">
              <Button
                size="xs"
                variant="light"
                color="orange"
                leftSection={<IconSend size={13} />}
                loading={postMutation.isPending}
                onClick={() => postMutation.mutate(analysis.id)}
              >
                Posten fehlgeschlagen – Retry
              </Button>
            </Tooltip>
          )}

          <Button
            size="xs"
            variant="light"
            color="violet"
            leftSection={<IconSparkles size={13} />}
            disabled={isAnalyzing}
            onClick={() => setDeepModalOpen(true)}
          >
            Deep-Analyse
          </Button>

          <Tooltip label={isNewTicket ? 'Standard-Analyse neu starten' : 'Manuelle Analyse starten'}>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconRefresh size={13} />}
              disabled={isAnalyzing}
              onClick={() => { setActiveMode('STANDARD'); triggerMutation.mutate({ mode: 'STANDARD' }); }}
            >
              {analysis ? 'Neu' : 'Analysieren'}
            </Button>
          </Tooltip>
        </Group>
      </Stack>

      {deepModal}
    </Paper>
  );
}
